import {
  differenceInCalendarDays,
  differenceInMinutes,
  parseISO,
} from 'date-fns'

import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_HOURS_FORMAT,
  DAY_FIRST_HOUR,
  DAY_MINUTES,
} from '../../constants/date'
import { createDate } from '../../utils/date'
import { formatDate } from '../Table'

import {
  CALENDAR_GRID_DAY_END_TIME,
  CALENDAR_GRID_MIDNIGHT_TIME,
  CALENDAR_GRID_MINUTES_PER_ROW,
  CALENDAR_GRID_RANGE_SEPARATOR,
  CALENDAR_GRID_TIME_SEPARATOR,
} from './CalendarGrid.constants'

function formatCalendarGridDate({ dateFormat, locale, timezone, value }) {
  return formatDate({
    date: value,
    dateFormat,
    locale,
    timezone,
    useTodayFormat: false,
  })
}

function getCalendarGridLocalTime({ locale, timezone, value }) {
  return formatCalendarGridDate({
    dateFormat: DATE_HOURS_FORMAT,
    locale,
    timezone,
    value,
  })
    .split(CALENDAR_GRID_TIME_SEPARATOR)
    .map(Number)
}

function formatCalendarGridTime({ locale, timezone, value }) {
  return formatCalendarGridDate({
    dateFormat: DATE_HOURS_FORMAT,
    locale,
    timezone,
    value,
  })
}

// An end is always after its start, so an end at midnight closes the day before
function formatCalendarGridEndTime({ locale, timezone, value }) {
  const time = formatCalendarGridTime({ locale, timezone, value })

  return time === CALENDAR_GRID_MIDNIGHT_TIME
    ? CALENDAR_GRID_DAY_END_TIME
    : time
}

function getCalendarGridTimeParts({ end, locale, start, timezone }) {
  const startTime = formatCalendarGridTime({ locale, timezone, value: start })

  return end
    ? [startTime, formatCalendarGridEndTime({ locale, timezone, value: end })]
    : [startTime]
}

function getCalendarGridLocalDayCount({ end, locale, start, timezone }) {
  return differenceInCalendarDays(
    parseISO(
      formatCalendarGridDate({
        dateFormat: DATE_API_DATA_FORMAT_SHORT,
        locale,
        timezone,
        value: end,
      })
    ),
    parseISO(
      formatCalendarGridDate({
        dateFormat: DATE_API_DATA_FORMAT_SHORT,
        locale,
        timezone,
        value: start,
      })
    )
  )
}

export function getCalendarGridItemDay({ item, locale, timezone }) {
  return item.isAllDay
    ? item.date
    : formatCalendarGridDate({
        dateFormat: DATE_API_DATA_FORMAT_SHORT,
        locale,
        timezone,
        value: item.start,
      })
}

export function getCalendarGridTimeRangeLabel({
  end,
  locale,
  start,
  timezone,
}) {
  return getCalendarGridTimeParts({ end, locale, start, timezone }).join(
    CALENDAR_GRID_RANGE_SEPARATOR
  )
}

export function getCalendarGridItemTimeParts({ item, locale, t, timezone }) {
  return item.isAllDay
    ? [t('Calendar.AllDayKey')]
    : getCalendarGridTimeParts({
        end: item.end,
        locale,
        start: item.start,
        timezone,
      })
}

export function getCalendarGridItemTimeLabel({ item, locale, t, timezone }) {
  return getCalendarGridItemTimeParts({ item, locale, t, timezone }).join(
    CALENDAR_GRID_RANGE_SEPARATOR
  )
}

export function getCalendarGridLocalHour({ locale, timezone, value }) {
  const [hour] = getCalendarGridLocalTime({ locale, timezone, value })

  return hour
}

export function getCalendarGridMinuteOffset({
  firstHour,
  locale,
  timezone,
  value,
}) {
  const [hours, minutes] = getCalendarGridLocalTime({
    locale,
    timezone,
    value,
  })

  return (hours - firstHour) * CALENDAR_GRID_MINUTES_PER_ROW + minutes
}

export function getCalendarGridDayTime({ day, minutes, timezone }) {
  const date = parseISO(day)

  return createDate(
    timezone,
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    DAY_FIRST_HOUR,
    minutes
  )
}

export function getCalendarGridItemDuration(item) {
  return item.end
    ? differenceInMinutes(parseISO(item.end), parseISO(item.start))
    : CALENDAR_GRID_MINUTES_PER_ROW
}

// On the local clock, as the hour rows are, not in elapsed minutes: a day with
// a clock change keeps its blocks on their hours. A span whose clock end is not
// after its start (inside the repeated autumn hour) keeps its elapsed length.
export function getCalendarGridItemMinuteOffsets({
  firstHour,
  item,
  locale,
  timezone,
}) {
  const startOffset = getCalendarGridMinuteOffset({
    firstHour,
    locale,
    timezone,
    value: item.start,
  })

  const clockEndOffset = item.end
    ? getCalendarGridMinuteOffset({
        firstHour,
        locale,
        timezone,
        value: item.end,
      }) +
      getCalendarGridLocalDayCount({
        end: item.end,
        locale,
        start: item.start,
        timezone,
      }) *
        DAY_MINUTES
    : startOffset

  return {
    endOffset:
      clockEndOffset > startOffset
        ? clockEndOffset
        : startOffset + getCalendarGridItemDuration(item),
    startOffset,
  }
}
